import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import { IDrop } from "src/core/drops/api/drops.api";
import { getSingleOrder } from "src/core/drops/utils/inherited-settings";
import { IOrder } from "src/core/orders/api/orders.api";
import {
  OrderDate,
  useOrderDeliveryDates
} from "src/core/orders/hooks/useOrderDeliveryDates";
import { useOrderingAvailableForDay } from "src/core/orders/hooks/validation";
import useNextAvailableDeliveryDate from "src/core/orders/hooks/validation/useNextAvailableDeliveryDate";
import { IProduct } from "src/core/products/api/products.api";
import { useDisabledDeliveryDate } from "src/core/shared/hooks/useDisabledDeliveryDate";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import { useWarningDeliveryDates } from "src/core/shared/hooks/useWarningDeliveryDate";
import type { IStore } from "src/core/shared/store";
import { OrderDayPicker } from "src/library/components/DayPicker";
import { isDayValid } from "src/library/utils/date-helpers";

interface IOrderContextProps {
  drop: IDrop;
  product?: IProduct;
  orderVariantId?: number;
  handleDateChange: (date: Date | null) => void;
}

export interface CreateOrderCalendarRef {
  orderDates: OrderDate[];
  currentOrders: IOrder[];
}

const CreateOrderCalendar: React.ForwardRefRenderFunction<CreateOrderCalendarRef, IOrderContextProps> = ({
  product,
  drop,
  orderVariantId,
  handleDateChange
}, ref) => {
  const { orderVariants, leadTimeGroups } = useMappedState(({ global }: IStore) => ({
    terminology: global.settings?.terminology,
    installedModules: global.settings?.installedModules || [],
    orderVariants: global.settings?.orderVariants || [],
    leadTimeGroups: global.settings?.leadTimeGroups || []
  }));

  const orderingNotAvailableForDay = useOrderingAvailableForDay(drop);
  const { minimumLeadTime } = useMinimumLeadTime();
  const [ { orderDates, currentOrders }, { getCalendarOrderDates } ] = useOrderDeliveryDates();
  const singleDailyDelivery = getSingleOrder(drop.groupedSettings);
  const isDisabled = useDisabledDeliveryDate(drop);
  const isWarning = useWarningDeliveryDates(drop);

  const selectedOrderVariant = React.useMemo(() => {
    return _.find(orderVariants, ov => ov.id === orderVariantId) ||
    _.find(drop.variantsAvailable, va => va.default) ||
    drop.variantsAvailable && drop.variantsAvailable[ 0 ];
  }, [
    orderVariants,
    drop.variantsAvailable,
    orderVariantId
  ]);

  const selectedLeadTimeGroup = React.useMemo(() => {
    return _.find(leadTimeGroups, ltg => ltg.id === selectedOrderVariant?.defaultProductLeadTimeGroupId);
  }, [ leadTimeGroups, selectedOrderVariant?.defaultProductLeadTimeGroupId ]);

  const nextAvailableDeliveryDate = useNextAvailableDeliveryDate(drop);

  const minimumDate = React.useMemo(() => {
    if (!selectedOrderVariant) {
      return moment.utc().add(360, "days").toDate();
    }

    if (selectedLeadTimeGroup) {
      return nextAvailableDeliveryDate(selectedLeadTimeGroup, selectedOrderVariant.id);
    } else if (minimumLeadTime) {
      return nextAvailableDeliveryDate(minimumLeadTime, selectedOrderVariant.id);
    }

    return new Date();
  }, [
    minimumLeadTime,
    nextAvailableDeliveryDate,
    selectedLeadTimeGroup,
    selectedOrderVariant
  ]);

  const [ currentMonth, setCurrentMonth ] = React.useState<{
    month: number;
    year: number;
  }>({
    month: minimumDate.getMonth(),
    year: minimumDate.getFullYear()
  });

  React.useEffect(() => {
    getCalendarOrderDates(drop.id, currentMonth);
  }, [
    currentMonth,
    getCalendarOrderDates,
    drop.id
  ]);

  React.useImperativeHandle(ref, () => {
    return {
      orderDates: [ ...orderDates ],
      currentOrders: [ ...currentOrders ]
    };
  }, [ currentOrders, orderDates ]);

  return (
    <>
      <OrderDayPicker
        singleDailyDelivery={singleDailyDelivery ? singleDailyDelivery.value : false}
        orderDates={orderDates}
        date={minimumDate}
        handleDateChange={day => {
          if (isDayValid(day, []) && selectedOrderVariant && orderingNotAvailableForDay(day, selectedOrderVariant, product) === null) {
            handleDateChange(day);
          } else {
            handleDateChange(null);
          }
        }}
        handleMonthChange={(month: Date) => {
          setCurrentMonth({
            month: month.getMonth(),
            year: month.getFullYear()
          });
        }}
        disabled={date => !selectedOrderVariant ? { isDisabled: true } : isDisabled(date, currentOrders, selectedOrderVariant, product)}
        fromDate={minimumDate}
        warning={date => !selectedOrderVariant ? { isWarning: true } : isWarning(date, selectedOrderVariant.id, product)}
        toDate={moment
          .utc()
          .add(2, "years")
          .toDate()}
      />
    </>
  );
};

export default React.forwardRef(CreateOrderCalendar);
